import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../lib/auth";
import api from "../lib/api";
import { BrainCircuit, SlidersHorizontal, FileSearch, Briefcase, Pencil, Check, Zap, ArrowRight, Play, Sparkles, ChevronRight, Users, Mic, Shield, Layers, Lock } from "lucide-react";
import { toast } from "sonner";
import FeedbackWidget from "../components/FeedbackWidget";
import UpgradeModal from "../components/UpgradeModal";
import CompanyBillingBanner from "../components/CompanyBillingBanner";

const STATUS_LABEL = { in_progress: "In Progress", completed: "Completed", pending: "Pending" };
const ROUND_LABEL = { hr: "HR", technical: "Tech", behavioral: "Behav", custom: "Custom" };

/* ══════════════════════════════════════════════════════════════ */
/*  Smart next-action hero — adapts to the candidate's state        */
/* ══════════════════════════════════════════════════════════════ */
function SmartHero({ interviews, avg }) {
  const navigate = useNavigate();
  const [starting, setStarting] = useState(false);

  const inProgress = interviews.find((i) => i.status === "in_progress" && i.source !== "job");
  const onboardingIv = interviews.find((i) => i.source === "onboarding");
  const hasRealDone = interviews.some((i) => i.status === "completed" && i.source !== "job" && i.source !== "onboarding");

  const startDemo = async () => {
    setStarting(true);
    try {
      const { data } = await api.post("/interviews/onboarding");
      if (data.skip) { setStarting(false); return; }
      navigate(`/auto-interview/${data.interview_id}`);
    } catch {
      toast.error("Couldn't start demo interview. Try again.");
      setStarting(false);
    }
  };

  // Decide which variant to show
  let variant;
  if (inProgress) {
    const isDemo = inProgress.source === "onboarding";
    variant = {
      eyebrow: "CONTINUE WHERE YOU LEFT OFF",
      title: isDemo ? "Your demo interview is waiting." : `Resume: ${inProgress.job_title || "your interview"}`,
      body: isDemo
        ? "You started the 5-minute demo — pick up right where you paused."
        : "You have a session in progress. Jump back in before momentum fades.",
      cta: isDemo ? "Continue demo" : "Resume interview",
      onClick: () => navigate(`/auto-interview/${inProgress.interview_id}`),
      icon: Play,
    };
  } else if (!hasRealDone && onboardingIv?.status !== "completed") {
    variant = {
      eyebrow: "FREE DEMO · NO SETUP REQUIRED",
      title: "Your first interview takes 5 minutes.",
      body: "No resume, no configuration — just 3 quick questions so you can feel AI-powered feedback before a full session.",
      cta: starting ? "Setting up…" : "Try it now — 3 questions",
      onClick: startDemo,
      icon: Sparkles,
      disabled: starting,
    };
  } else {
    variant = {
      eyebrow: "READY FOR ANOTHER ROUND?",
      title: avg !== "—" ? `You're averaging ${avg}/100. Push it higher.` : "Ready for your next session?",
      body: "Start a fresh mock interview, practise by role, and keep your delivery sharp.",
      cta: "Start a mock interview",
      onClick: () => navigate("/interviews/new"),
      icon: BrainCircuit,
    };
  }

  const Icon = variant.icon;
  return (
    <div className="relative overflow-hidden rounded-2xl bg-[#0F172A] text-white p-7 md:p-8">
      <div className="pointer-events-none absolute -top-24 -right-20 w-80 h-80 rounded-full"
        style={{ background: "radial-gradient(circle, rgba(99,102,241,0.22) 0%, rgba(37,99,235,0.12) 45%, transparent 70%)" }} />
      <div className="relative">
        <div className="font-mono text-[10px] text-[#818CF8] tracking-[0.22em] mb-3">{variant.eyebrow}</div>
        <h2 className="font-display font-black text-2xl md:text-[1.75rem] leading-tight tracking-tight mb-2 max-w-xl">{variant.title}</h2>
        <p className="text-white/55 text-sm leading-relaxed mb-6 max-w-lg">{variant.body}</p>
        <button onClick={variant.onClick} disabled={variant.disabled}
          className="inline-flex items-center gap-2 rounded-xl bg-white text-[#0F172A] hover:bg-white/90 px-6 py-3 text-sm font-bold shadow-lg transition-all disabled:opacity-60">
          <Icon className="w-4 h-4" /> {variant.cta}
        </button>
      </div>
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════ */
/*  Tool / action cards                                             */
/* ══════════════════════════════════════════════════════════════ */
function ActionCard({ icon: Icon, tag, title, subtitle, to, accentColor }) {
  return (
    <Link to={to} className="block h-full">
      <div className="rounded-2xl border border-border bg-white p-5 h-full hover:border-[#0025D8]/30 hover:shadow-lg hover:shadow-[#0F172A]/5 hover:-translate-y-0.5 transition-all cursor-pointer">
        <div className={`w-11 h-11 rounded-xl flex items-center justify-center mb-4 ${accentColor || "bg-[#0025D8]/10"}`}>
          <Icon className={`w-5 h-5 ${accentColor ? "text-white" : "text-[#0025D8]"}`} />
        </div>
        <div className="font-mono text-[10px] text-muted-foreground tracking-[0.2em] mb-1">{tag}</div>
        <div className="font-display font-bold text-lg text-[#0F172A]">{title}</div>
        <p className="text-xs text-muted-foreground mt-1 leading-relaxed">{subtitle}</p>
      </div>
    </Link>
  );
}

// Autopilot's dashboard tile. Stateful like ResumeOptimizerCard below, because a card that reads
// the same whether you have 100 jobs waiting or have never opened it is just a menu item.
//
// MATCHES LEAD. When several things are true at once the headline is the match count — that is the
// number candidates come back for. But work that is STALLED must not disappear behind it, so drafts
// awaiting approval get their own line underneath: those applications are already filled in, already
// cost real LLM calls, and will never be sent unless someone acts on them.
//
// That second line is a SEPARATE <Link> rather than nested inside the card's — an <a> inside an <a>
// is invalid HTML and browsers resolve it unpredictably. Hence the wrapper div holding two links.
function AutopilotCard({ summary, usage }) {
  const { onboarded = false, has_titles = false, matches = 0, needs_review = 0, applied = 0 } = summary || {};

  // The free tier's one-time allowance. Shown only while it IS one-time: a monthly entitlement
  // refills and does not need counting down on a dashboard, but a trial that never resets is
  // something the candidate should see before they spend it, not after.
  const trial = usage?.ai_applies_window === "lifetime" ? {
    left: Math.max(0, (usage.ai_applies_limit || 0) - (usage.ai_applies_used ?? 0)),
    limit: usage.ai_applies_limit || 0,
  } : null;

  // `summary` is null when the request failed — which is NOT the same as "new candidate". Claiming
  // someone needs to set up Autopilot because a fetch blipped would send an established user back
  // through onboarding, so the unknown case gets its own neutral state pointing at the hub (which
  // redirects to setup by itself if they really haven't onboarded).
  let state;
  if (!summary)         state = "unknown";
  else if (!onboarded)  state = "setup";       // nothing works until the wizard is done
  else if (!has_titles) state = "no_titles";   // onboarded but nothing to search for
  else if (matches > 0) state = "matches";
  else if (applied > 0) state = "applied";     // worked through the pool; next batch pending
  else                  state = "building";

  const cfg = {
    setup:     { tag: "APPLY ON AUTOPILOT", body: "Let AI find matching roles and fill in every application — you approve before anything is sent.", cta: "Set up Autopilot →", to: "/apply/setup" },
    no_titles: { tag: "ONE STEP LEFT",      body: "Add the roles you're targeting and we'll start matching jobs to your profile.",                   cta: "Add your roles →",   to: "/apply" },
    matches:   { tag: "JOBS MATCHED",       body: "Roles matched to your profile, ranked best first and ready to apply.",                            cta: "See your matches →", to: "/apply", n: matches },
    applied:   { tag: "APPLICATIONS SENT",  body: "You're through this batch. We'll keep matching new roles as they're posted.",                     cta: "Track applications →", to: "/apply/tracker", n: applied },
    building:  { tag: "APPLY ON AUTOPILOT", body: "We're matching roles to your profile — your first batch will appear here shortly.",               cta: "Open Autopilot →",   to: "/apply" },
    unknown:   { tag: "APPLY ON AUTOPILOT", body: "AI finds matching roles and fills in every application — you approve before anything is sent.",   cta: "Open Autopilot →",   to: "/apply" },
  }[state];

  return (
    <div className="rounded-2xl border border-border bg-white h-full flex flex-col overflow-hidden hover:border-[#0025D8]/30 hover:shadow-lg hover:shadow-[#0F172A]/5 hover:-translate-y-0.5 transition-all">
      <Link to={cfg.to} className="block p-5 flex-1">
        <div className="w-11 h-11 rounded-xl flex items-center justify-center mb-4 bg-[#6D28D9]">
          <Zap className="w-5 h-5 text-white" />
        </div>
        <div className="font-mono text-[10px] text-muted-foreground tracking-[0.2em] mb-1">{cfg.tag}</div>
        {cfg.n != null ? (
          <div className="flex items-baseline gap-1.5 mb-1">
            <span className="font-display font-bold text-2xl text-[#6D28D9]">{cfg.n}</span>
            <span className="font-mono text-[10px] text-muted-foreground">
              {state === "matches" ? (cfg.n === 1 ? "job" : "jobs") : cfg.n === 1 ? "application" : "applications"}
            </span>
          </div>
        ) : (
          <div className="font-display font-bold text-lg text-[#0F172A]">Autopilot</div>
        )}
        <p className="text-xs text-muted-foreground leading-relaxed">{cfg.body}</p>
        <div className="mt-3 font-mono text-[10px] text-[#0025D8] font-bold">{cfg.cta}</div>
      </Link>

      {trial && (
        <Link to="/apply"
          className={`flex items-center gap-2 px-5 py-2.5 border-t border-border transition-colors ${
            trial.left === 0 ? "bg-[#E11D48]/[0.06] hover:bg-[#E11D48]/[0.12]"
                             : "bg-[#6D28D9]/[0.06] hover:bg-[#6D28D9]/[0.12]"}`}>
          <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${
            trial.left === 0 ? "bg-[#E11D48]" : "bg-[#6D28D9]"}`} />
          <span className={`text-[11.5px] font-semibold ${
            trial.left === 0 ? "text-[#E11D48]" : "text-[#5B21B6]"}`}>
            {trial.left === 0
              ? `All ${trial.limit} free applications used — upgrade for more`
              : `${trial.left} of ${trial.limit} free applications left`}
          </span>
        </Link>
      )}

      {needs_review > 0 && (
        <Link to="/apply/tracker"
          className="flex items-center gap-2 px-5 py-2.5 border-t border-border bg-[#F59E0B]/[0.07] hover:bg-[#F59E0B]/[0.14] transition-colors">
          <span className="w-1.5 h-1.5 rounded-full bg-[#F59E0B] flex-shrink-0" />
          <span className="text-[11.5px] text-[#B45309] font-semibold">
            {needs_review} {needs_review === 1 ? "application is" : "applications are"} waiting for your approval
          </span>
        </Link>
      )}
    </div>
  );
}


function ResumeOptimizerCard({ summary }) {
  const { saved_count = 0, latest_ats_score = null, has_optimization = false, placeholders_remaining = 0 } = summary || {};

  let state = "none";
  if (!summary)                                            state = "none";
  else if (saved_count === 0)                              state = "none";
  else if (latest_ats_score === null)                      state = "unscored";
  else if (has_optimization && placeholders_remaining > 0) state = "placeholders";
  else if (latest_ats_score < 60)                         state = "low";
  else if (latest_ats_score < 80)                         state = "medium";
  else                                                     state = "ready";

  const cfg = {
    none:         { tag: "RESUME OPTIMIZER", iconBg: "bg-[#0F172A]", body: "Get your resume past ATS filters — score it against any job description, free.",                                                                cta: "Score your resume →",  to: "/resume-analyzer" },
    unscored:     { tag: "RESUME OPTIMIZER", iconBg: "bg-[#0025D8]", body: `You have ${saved_count} saved resume${saved_count !== 1 ? "s" : ""}. See how ${saved_count === 1 ? "it" : "they"} score against a job.`,       cta: "Run ATS analysis →",   to: "/resume-analyzer" },
    low:          { tag: "ATS SCORE · LOW",  iconBg: "bg-[#E11D48]", scoreColor: "#E11D48", body: "Your resume may not pass ATS filters. AI can rewrite it with the right keywords.",                                      cta: "Optimise with AI →",   to: "/resume-analyzer" },
    medium:       { tag: "ATS SCORE · GOOD", iconBg: "bg-[#F59E0B]", scoreColor: "#F59E0B", body: "Good score — but there's room to improve. AI can push this higher with targeted rewrites.",                             cta: "Improve score →",      to: "/resume-analyzer" },
    placeholders: { tag: "ACTION NEEDED",    iconBg: "bg-[#F59E0B]", scoreColor: "#10B981", body: `${placeholders_remaining} value${placeholders_remaining !== 1 ? "s" : ""} still need your input before you can download.`, cta: "Finish resume →",      to: "/resumes"         },
    ready:        { tag: "RESUME READY",     iconBg: "bg-[#10B981]", scoreColor: "#10B981", body: "Your resume is ATS-optimised and ready to download as a professional PDF.",                                             cta: "View templates →",     to: "/resumes"         },
  }[state];

  return (
    <Link to={cfg.to} className="block h-full">
      <div className="rounded-2xl border border-border bg-white p-5 h-full hover:border-[#0025D8]/30 hover:shadow-lg hover:shadow-[#0F172A]/5 hover:-translate-y-0.5 transition-all cursor-pointer">
        <div className={`w-11 h-11 rounded-xl flex items-center justify-center mb-4 ${cfg.iconBg}`}>
          <FileSearch className="w-5 h-5 text-white" />
        </div>
        <div className="font-mono text-[10px] text-muted-foreground tracking-[0.2em] mb-1">{cfg.tag}</div>
        {latest_ats_score != null ? (
          <div className="flex items-baseline gap-1 mb-1">
            <span className="font-display font-bold text-2xl" style={{ color: cfg.scoreColor }}>{latest_ats_score}</span>
            <span className="font-mono text-[10px] text-muted-foreground">/100</span>
          </div>
        ) : (
          <div className="font-display font-bold text-lg text-[#0F172A]">Resume Optimizer</div>
        )}
        <p className="text-xs text-muted-foreground leading-relaxed">{cfg.body}</p>
        <div className="mt-3 font-mono text-[10px] text-[#0025D8] font-bold">{cfg.cta}</div>
      </div>
    </Link>
  );
}

/* ══════════════════════════════════════════════════════════════ */
/*  Right-rail cards                                                */
/* ══════════════════════════════════════════════════════════════ */
function RailCard({ label, children, action }) {
  return (
    <div className="rounded-2xl border border-border bg-white p-5">
      <div className="flex items-center justify-between mb-3">
        <div className="font-mono text-[10px] text-muted-foreground tracking-[0.22em]">{label}</div>
        {action}
      </div>
      {children}
    </div>
  );
}

function PlanRailCard({ planUsage, onUpgrade }) {
  if (!planUsage) return null;
  if (planUsage.plan === "premium") {
    return (
      <div className="rounded-2xl border border-[#7C3AED]/30 bg-[#7C3AED]/5 p-5">
        <div className="flex items-center gap-2 mb-1">
          <Zap className="w-4 h-4 text-[#7C3AED]" />
          <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-[#7C3AED] font-bold">Premium</span>
        </div>
        <p className="text-xs text-muted-foreground mb-3">Unlimited interviews · all roles · no session limit.</p>
        <Link to="/subscription"
          className="w-full inline-flex items-center justify-center gap-1.5 rounded-xl border border-[#7C3AED]/40 text-[#7C3AED] hover:bg-[#7C3AED]/10 px-4 py-2 text-xs font-semibold transition-colors">
          Manage subscription →
        </Link>
      </div>
    );
  }
  const used = planUsage.interviews_this_month;
  const limit = planUsage.interviews_limit;
  return (
    <RailCard label="FREE PLAN">
      <div className="flex items-center gap-2 mb-2">
        <div className="flex gap-1 flex-1">
          {Array.from({ length: limit }).map((_, i) => (
            <div key={i} className={`h-1.5 flex-1 rounded-full ${i < used ? "bg-[#0025D8]" : "bg-border"}`} />
          ))}
        </div>
      </div>
      <p className="text-xs text-muted-foreground mb-4">
        <span className="font-semibold text-[#0F172A]">{used} / {limit}</span> interviews used
        {planUsage.reset_date && <> · resets {new Date(planUsage.reset_date).toLocaleDateString("en", { month: "short", day: "numeric" })}</>}
      </p>
      <button onClick={onUpgrade}
        className="w-full inline-flex items-center justify-center gap-1.5 rounded-xl border border-[#7C3AED] text-[#7C3AED] hover:bg-[#7C3AED] hover:text-white px-4 py-2.5 text-xs font-bold transition-colors">
        <Zap className="w-3.5 h-3.5" /> Upgrade to Premium
      </button>
    </RailCard>
  );
}

function StatsRailCard({ sessions, completed, avg }) {
  const stats = [
    { label: "Sessions", value: sessions },
    { label: "Completed", value: completed },
    { label: "Avg score", value: avg },
  ];
  return (
    <RailCard label="YOUR PROGRESS">
      <div className="grid grid-cols-3 gap-2">
        {stats.map((s) => (
          <div key={s.label} className="rounded-xl bg-secondary/40 border border-border p-3 text-center">
            <div className="font-display font-black text-2xl text-[#0F172A]">{s.value}</div>
            <div className="text-[10px] text-muted-foreground mt-0.5">{s.label}</div>
          </div>
        ))}
      </div>
    </RailCard>
  );
}

function TargetRoleRailCard() {
  const { user, setUser } = useAuth();
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState(user?.target_job_title || "");
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.patch("/auth/me", { target_job_title: value.trim() || null });
      setUser(data);
      setEditing(false);
    } catch {} finally { setSaving(false); }
  };
  const cancel = () => { setValue(user?.target_job_title || ""); setEditing(false); };

  return (
    <RailCard
      label="TARGET ROLE"
      action={!editing && (
        <button onClick={() => { setValue(user?.target_job_title || ""); setEditing(true); }}
          className="text-muted-foreground hover:text-[#0025D8] transition-colors" title="Edit target role">
          <Pencil className="w-3 h-3" />
        </button>
      )}
    >
      {editing ? (
        <div className="flex items-center gap-2 flex-wrap">
          <input autoFocus type="text" value={value} onChange={(e) => setValue(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") save(); if (e.key === "Escape") cancel(); }}
            placeholder="e.g. QA Engineer"
            className="flex-1 min-w-0 rounded-lg border border-input bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#0025D8]/20 focus:border-[#0025D8]" />
          <button onClick={save} disabled={saving} className="text-[#0025D8] hover:opacity-70 disabled:opacity-40">
            <Check className="w-4 h-4" />
          </button>
          <button onClick={cancel} className="text-muted-foreground hover:text-foreground text-[10px] font-mono">Cancel</button>
        </div>
      ) : user?.target_job_title ? (
        <div className="flex items-center gap-2">
          <Briefcase className="w-4 h-4 text-[#0025D8] flex-shrink-0" />
          <span className="text-sm font-semibold text-[#0F172A] truncate">{user.target_job_title}</span>
        </div>
      ) : (
        <div>
          <p className="text-xs text-muted-foreground mb-3">Add your target role for personalised prep and job matches.</p>
          <button onClick={() => setEditing(true)}
            className="inline-flex items-center gap-1.5 rounded-xl border border-[#0025D8] text-[#0025D8] hover:bg-[#0025D8] hover:text-white px-3 py-2 text-xs font-bold transition-colors">
            Add target role <ArrowRight className="w-3 h-3" />
          </button>
        </div>
      )}
    </RailCard>
  );
}

/* ══════════════════════════════════════════════════════════════ */
/*  Dashboard                                                       */
/* ══════════════════════════════════════════════════════════════ */
export default function Dashboard() {
  const { user } = useAuth();
  const [interviews, setInterviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [feedbackOpen, setFeedbackOpen] = useState(false);
  const [feedbackDismissed, setFeedbackDismissed] = useState(false);
  const [resumeSummary, setResumeSummary] = useState(null);
  const [applySummary, setApplySummary] = useState(null);
  const [planUsage, setPlanUsage] = useState(null);
  const [upgradeOpen, setUpgradeOpen] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        if (user.role === "candidate") {
          const [iv, rs, pu, ap] = await Promise.all([
            api.get("/interviews"),
            api.get("/resume/summary").catch(() => ({ data: null })),
            api.get("/candidate/plan-usage").catch(() => ({ data: null })),
            api.get("/apply/summary").catch(() => ({ data: null })),
          ]);
          setInterviews(iv.data);
          setResumeSummary(rs.data);
          setPlanUsage(pu.data);
          setApplySummary(ap.data);
        }
      } finally { setLoading(false); }
    })();
  }, [user.role]);

  if (loading) return <div className="p-12 font-mono text-sm text-muted-foreground">Loading dashboard…</div>;

  // ─────────────── CANDIDATE ───────────────
  if (user.role === "candidate") {
    const visibleInterviews = interviews.filter((i) => i.source !== "job" && i.source !== "onboarding");
    const completed = visibleInterviews.filter((i) => i.status === "completed");
    const avg = completed.length ? Math.round(completed.reduce((s, i) => s + (i.overall_score || 0), 0) / completed.length) : "—";

    return (
      <main className="max-w-7xl mx-auto px-6 md:px-12 py-12">
        {/* Header */}
        <div className="mb-8">
          <div className="font-mono text-[10px] text-[#0025D8] tracking-[0.25em] mb-2">CANDIDATE CONTROL ROOM</div>
          <h1 className="font-display font-black text-4xl tracking-tight text-[#0F172A]">Hello, {user.name.split(" ")[0]}.</h1>
        </div>

        <UpgradeModal open={upgradeOpen} onClose={() => setUpgradeOpen(false)} />

        {/* App shell: main + rail */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">

          {/* ─── MAIN COLUMN ─── */}
          <div className="lg:col-span-2 space-y-8">
            <SmartHero interviews={interviews} avg={avg} />

            {/* Tools */}
            <section>
              <div className="font-mono text-[10px] text-muted-foreground tracking-[0.2em] mb-3">YOUR TOOLKIT</div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <AutopilotCard summary={applySummary} usage={planUsage} />
                <ActionCard icon={BrainCircuit} tag="MOST POPULAR" title="Mock Interviews" subtitle="Practise by role with curated per-round question banks" to="/interviews/new" accentColor="bg-[#0025D8]" />
                <ActionCard icon={SlidersHorizontal} tag="BRING YOUR OWN DOCS" title="Custom Interview" subtitle="Your own docs, AI question suggestions, hand-picked questions" to="/custom-roles" accentColor="bg-[#0F172A]" />
                <ResumeOptimizerCard summary={resumeSummary} />
              </div>
            </section>

            {/* Your interviews */}
            <section>
              <div className="flex items-center justify-between mb-3">
                <h2 className="font-display font-bold text-xl text-[#0F172A]">Your interviews</h2>
                {visibleInterviews.length > 0 && (
                  <Link to="/interviews/new" className="text-xs font-semibold text-[#0025D8] hover:underline">+ New interview</Link>
                )}
              </div>
              {visibleInterviews.length === 0 ? (
                <div className="rounded-2xl border border-dashed border-border p-10 text-center">
                  <p className="text-muted-foreground text-sm">No interviews yet. Use a tool above to get started.</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {visibleInterviews.map((iv) => {
                    const isCustom = iv.source === "custom_role";
                    const rounds = Object.entries(iv.modules || {})
                      .filter(([, m]) => m?.questions?.length > 0)
                      .map(([key]) => ROUND_LABEL[key] || key.toUpperCase());
                    return (
                      <div key={iv.interview_id} className="rounded-xl border border-border bg-white p-4 flex items-center justify-between gap-4 hover:border-[#0025D8]/30 hover:shadow-sm transition-all">
                        <div className="min-w-0">
                          <div className="flex items-center gap-1.5 mb-1.5 flex-wrap">
                            <span className={`font-mono text-[9px] uppercase tracking-[0.12em] px-1.5 py-0.5 rounded font-bold ${isCustom ? "bg-[#0F172A] text-white" : "bg-[#0025D8] text-white"}`}>
                              {isCustom ? "Custom" : "Mock"}
                            </span>
                            {rounds.map((r) => (
                              <span key={r} className="font-mono text-[9px] uppercase tracking-[0.1em] text-muted-foreground border border-border rounded px-1.5 py-0.5">{r}</span>
                            ))}
                          </div>
                          <div className="font-semibold text-[#0F172A] truncate">{iv.job_title}</div>
                          <div className="text-xs text-muted-foreground">{new Date(iv.created_at).toLocaleString()}</div>
                        </div>
                        <div className="flex items-center gap-3 sm:gap-5 flex-shrink-0">
                          <span className="hidden sm:inline-flex font-mono text-[10px] uppercase tracking-[0.1em] text-muted-foreground border border-border rounded-full px-2.5 py-1">
                            {STATUS_LABEL[iv.status] ?? iv.status}
                          </span>
                          <div className="text-right">
                            <div className="font-display font-bold text-lg text-[#0F172A]">{iv.overall_score ? Math.round(iv.overall_score) : "—"}</div>
                            <div className="font-mono text-[9px] text-muted-foreground">/100</div>
                          </div>
                          <Link
                            to={iv.status === "completed" ? `/interviews/${iv.interview_id}/results` : `/auto-interview/${iv.interview_id}`}
                            className="inline-flex items-center gap-1 text-xs font-semibold text-[#0025D8] border border-[#0025D8]/30 rounded-lg px-3 py-1.5 hover:bg-[#0025D8] hover:text-white transition-colors"
                            data-testid={`open-iv-${iv.interview_id}`}>
                            {iv.status === "completed" ? "Results" : "Continue"} <ChevronRight className="w-3 h-3" />
                          </Link>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </section>

            {/* Feedback */}
            <div className="border-t border-border pt-6">
              {!feedbackDismissed && !feedbackOpen && (
                <p className="text-xs text-muted-foreground">
                  Have feedback?{" "}
                  <button onClick={() => setFeedbackOpen(true)} className="text-[#0025D8] hover:underline font-medium">Share your thoughts →</button>
                </p>
              )}
              {feedbackOpen && !feedbackDismissed && (
                <div className="max-w-xl">
                  <FeedbackWidget contextType="dashboard" onDismiss={() => { setFeedbackDismissed(true); setFeedbackOpen(false); }} />
                </div>
              )}
            </div>
          </div>

          {/* ─── RIGHT RAIL ─── */}
          <aside className="space-y-5">
            <PlanRailCard planUsage={planUsage} onUpgrade={() => setUpgradeOpen(true)} />
            <StatsRailCard sessions={visibleInterviews.length} completed={completed.length} avg={avg} />
            <TargetRoleRailCard />
          </aside>
        </div>
      </main>
    );
  }

  // ─────────────── COMPANY locked (pending / approved / past_due / canceled) ───────────────
  const companyStatus = user?.company_billing?.company_status;
  const isLockedCompany = companyStatus && companyStatus !== "active";

  if (isLockedCompany) {
    const LOCKED_FEATURES = [
      { icon: Briefcase, label: "Post roles", desc: "Define open positions and manage JD, seniority, and requirements." },
      { icon: Users, label: "Candidate pipeline", desc: "Add candidates, upload resumes, and track their progress." },
      { icon: Mic, label: "AI interview invites", desc: "Send AI-powered voice interviews and get scored transcripts." },
      { icon: SlidersHorizontal, label: "Talent pool", desc: "Search across all candidates you've ever evaluated." },
      { icon: Layers, label: "Integrations", desc: "Connect LinkedIn, ATS, and referral tracking." },
      { icon: Shield, label: "Consent management", desc: "Customise GDPR/CCPA candidate consent templates." },
    ];
    return (
      <main className="max-w-3xl mx-auto px-6 py-12">
        <div className="mb-8">
          <div className="font-mono text-[10px] text-[#0025D8] tracking-[0.25em] mb-2">ACCOUNT SETUP</div>
          <h1 className="font-display font-black text-4xl tracking-tight text-[#0F172A]">Welcome, {user.name} 👋</h1>
          <p className="text-muted-foreground text-sm mt-2 max-w-lg">
            Thanks for signing up! Your account needs to be activated before you can post roles or run interviews.
            Submit the request below and our team will get you set up — usually within one business day.
          </p>
        </div>

        <CompanyBillingBanner />

        <div className="mt-8">
          <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground mb-4 flex items-center gap-2">
            <Lock className="w-3 h-3" /> Features unlocked after activation
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {LOCKED_FEATURES.map(({ icon: Icon, label, desc }) => (
              <div key={label} className="flex items-start gap-3 rounded-xl border border-border bg-secondary/40 px-4 py-3.5 opacity-60">
                <Icon className="w-4 h-4 text-muted-foreground flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-semibold text-foreground">{label}</p>
                  <p className="text-xs text-muted-foreground mt-0.5">{desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </main>
    );
  }

  // ─────────────── COMPANY active ───────────────
  return (
    <main className="max-w-7xl mx-auto px-6 md:px-12 py-12">
      <div className="mb-8">
        <div className="font-mono text-[10px] text-[#0025D8] tracking-[0.25em] mb-2">HIRING CONTROL</div>
        <h1 className="font-display font-black text-4xl tracking-tight text-[#0F172A]">{user.name}</h1>
      </div>

      <div className="relative overflow-hidden rounded-2xl bg-[#0F172A] text-white p-8 flex items-center justify-between gap-6 flex-wrap">
        <div className="pointer-events-none absolute -top-20 -right-16 w-72 h-72 rounded-full"
          style={{ background: "radial-gradient(circle, rgba(99,102,241,0.20) 0%, transparent 70%)" }} />
        <div className="relative">
          <div className="font-mono text-[10px] text-[#818CF8] tracking-[0.22em] mb-2">ROLE MANAGEMENT</div>
          <h2 className="font-display font-bold text-2xl mb-1">Manage your hiring pipeline</h2>
          <p className="text-sm text-white/55 max-w-md">Post roles, add candidates, score resumes, and set up AI-powered interviews — all in one place.</p>
        </div>
        <Link to="/company/roles" className="relative flex-shrink-0">
          <span className="inline-flex items-center gap-2 rounded-xl bg-white text-[#0F172A] hover:bg-white/90 px-6 py-3 text-sm font-bold shadow-lg transition-all">
            Go to Roles <ArrowRight className="w-4 h-4" />
          </span>
        </Link>
      </div>
    </main>
  );
}
